/*
 * FusionMSI (Fusion Medical SuperIntelligence) marketing content — PROVISIONAL copy.
 * Edit here, not in JSX. Language reflects what is built today.
 *
 * Paths: fusionmsi.com uses clean routes (/display, /auth, …); app hosts use /dp-*.
 * See fusionMsiMarketingRouting.js. Markdown reference: FUSIONMSI_MARKETING_MATERIALS.md (repo root).
 */

import {
  PRODUCT_NAME,
  PRODUCT_NAME_LONG,
  PRODUCT_NAME_SPOKEN,
  PRODUCT_TAGLINE,
  productNameForSpeech,
} from '../data/fusionMsiBranding';

export {
  PRODUCT_NAME,
  PRODUCT_NAME_LONG,
  PRODUCT_NAME_SPOKEN,
  PRODUCT_TAGLINE,
  productNameForSpeech,
};

/** Shown on the splash page while the product is in private beta. */
export const PROVISIONAL = {
  badge: 'Private beta · Provisional preview',
  notice:
    'FusionMSI is live in limited preview. Clinical records are being imported and physician-reviewed; most content ships as draft until dual verification is complete. Features and copy will change.',
};

export const HERO = {
  eyebrow: 'Emergency medicine · Invite-only preview',
  title: 'FusionMSI',
  titleGold: PRODUCT_TAGLINE,
  subtitle:
    'Ask out loud at the bedside. Fusion Medical SuperIntelligence retrieves physician-curated text — it does not invent doses or differentials. Numbers and nuance stay on screen where you can read them.',
  ctaPrimary: 'Sign in',
};

/** Hub cards linking to sub-pages. */
export const HUB_PAGES = [
  {
    slug: 'how-it-works',
    title: 'How it works',
    teaser:
      'Voice-first bedside retrieval, retrieve-never-generate safety, and EM-native record structure.',
  },
  {
    slug: 'capabilities',
    title: 'Capabilities',
    teaser:
      'What is in this preview — bedside voice, content library, authoring tools, and example commands.',
  },
  {
    slug: 'access',
    title: 'Access & preview limits',
    teaser: 'Invite-only beta access, provisional limitations, and how clinicians get started.',
  },
];

/** Core positioning — qualified for beta state. */
export const SECTIONS = [
  {
    id: 'voice-first',
    title: 'Voice-first at the bedside',
    body:
      'In DecisionPoint, start a session and speak naturally — e.g. “chest pain” or “give me the overview.” Keywords load records onto the document stack (screen-only). “Give me the overview” scrolls the open record to the top; it does not speak `spoken_intro` or key labels. Full detail, doses, and intervals appear on screen. A pre-playback guard is available for any future clinical TTS; retrieve/overview/read_section currently return empty speech.',
  },
  {
    id: 'verified-not-generated',
    title: 'Retrieve, never generate',
    body:
      'Bedside answers come from stored clinical records, spoken verbatim — not from an LLM improvising management. Content moves through two physician gates (accuracy and completeness) before it is treated as fully verified. Until then, an UNVERIFIED watermark is shown at bedside.',
  },
  {
    id: 'built-for-the-box',
    title: 'Structured for EM thinking',
    body:
      'Records are organized as presentations, linked diagnoses, and decision rules — with can’t-miss lists, workup, disposition logic, and calculator mappings. Authors edit label, screen detail, and TTS side by side so voice and display stay aligned.',
  },
];

/** Concrete capabilities aligned to shipped routes and code. */
export const CAPABILITY_GROUPS = [
  {
    id: 'bedside',
    title: 'DecisionPoint (invite required)',
    items: [
      'Voice session in DecisionPoint with live transcript and mic modes (always on, mute).',
      'Curated voice triggers for chest pain, syncope, dyspnea, abdominal pain, and headache; plus overview and read workup/management/disposition that scroll the active record (screen-only, not spoken).',
      'Patient sex and age band selectors — demographic symptom-matrix slices when signed in.',
      'Document stack — browse matched presentations with on-screen ← → navigation.',
      'On-screen presentation view with linked diagnosis context and key-item highlighting.',
      'Misretrieval logging (“wrong record”) to prioritize content fixes.',
      'On-device hash re-check — tampered or stale verified content falls back to unverified display.',
    ],
  },
  {
    id: 'content',
    title: 'Content library (preview)',
    items: [
      'Import pipeline seeds draft presentations from 15 can’t-miss chief complaints and decision rules from the calculator catalog (Wells, HEART, Ottawa, and others).',
      'Symptom matrix (~22k demographic presentations in Firestore) — voice catalog matching when signed in.',
      'Dual verification gates: content (accuracy) and completeness (nothing missing) — each signed separately by assigned reviewers.',
      'Optional AI augment proposes field-level edits; nothing is auto-applied or auto-verified.',
      'Compiled offline bundles when snapshots are exported and built — otherwise live Firestore retrieval.',
    ],
  },
  {
    id: 'admin',
    title: 'Authoring & oversight (beta access)',
    items: [
      'Admin hub — browse and edit presentations, diagnoses, decision rules, and medications.',
      'Reviewer inbox filtered by gate, chapter, provenance, and stale-after-edit.',
      'Idempotent import of draft records from existing clinical seeds.',
      'Coverage matrix and immutable audit log for platform administrators.',
      'Invite-based access roster — time-limited beta links via the join flow.',
      'AI Augment governed by the platform Query Manager — global, regional, and institution-wide settings for `dp-record-augment`; proposals only, never auto-applied.',
    ],
  },
];

export const VOICE_COMMANDS = {
  title: 'Example voice commands (English)',
  intro: 'After starting a bedside session, try:',
  examples: [
    { phrase: '“Chest pain” / “ACS”', detail: 'Open the chest pain presentation' },
    { phrase: '“Syncope” / “passed out”', detail: 'Open syncope' },
    { phrase: '“Dyspnea” / “shortness of breath”', detail: 'Open dyspnea' },
    { phrase: '“Abdominal pain”', detail: 'Open abdominal pain' },
    { phrase: '“Headache”', detail: 'Open headache' },
    { phrase: '“Give me the overview”', detail: 'Scroll the current record to the top (screen-only — does not speak key labels)' },
    { phrase: '“Read the workup” / “management” / “disposition”', detail: 'Scroll to that section when present (screen-only)' },
  ],
};

export const LIMITATIONS = {
  title: 'Preview limitations (provisional)',
  items: [
    'Invite-only — no open signup; access is granted by your operator.',
    'Most imported content is draft until physicians complete both verification gates.',
    'Voice navigation is English-only in this preview; clinical text is not auto-translated.',
    'Speaker verification (“only listen to me”) is not fully enabled yet.',
    'Not a patient-facing tool; for licensed clinicians in professional workflow only.',
    'Requires network connectivity unless compiled bundles have been deployed for your environment.',
  ],
};

export const ACCESS = {
  title: 'Getting access',
  body:
    'Sign in from the marketing page, or open an invite link from your operator to activate beta access. On fusionmsi.com use /auth and /join; on app hosts use /dp-auth and /dp-join. Bedside and admin tools require an active invite; platform operators manage the roster separately.',
};

/** About Us — a note from the founder (long-form essay). */
export const ABOUT = {
  title: 'About Us',
  subtitle: 'A note from our founder',
  paragraphs: [
    'Satya Nadella recently articulated something worth building on: that the real opportunity in AI is not selecting the best model, but building a learning loop where human expertise and AI capability compound together — and that clinicians shape the loop inside the organization that generates it, rather than leaving it on the platform it runs on. It’s a compelling frame. What I want to do here is extend it into the space where I’ve spent my career, and add something to it that comes specifically from practicing medicine across a range of settings: a conviction about the patient.',
    'For as long as I’ve been seeing patients — in emergency departments in Chicago and Boston, in a rural Wisconsin community, in mission hospitals in Kenya — one of the most persistent and unnecessary sources of clinical risk has been this: patients cannot reliably convey their own health information from one provider to the next. They arrive without their records. They reconstruct their medication list from memory while in pain, or frightened, or simply exhausted. They report a “blood pressure pill” and a “water pill” and something their cardiologist added six months ago whose name they never retained. Their primary care physician has one list, their specialist has another, and the emergency department is starting from scratch.',
    'This is not a technology failure in the sophisticated sense. It is a structural failure of basic information custody. The patient’s health record has always belonged, in practice, to whoever last generated it — scattered across institutions, inaccessible at the moment of need, requiring reconstruction at every transition. A single accurate medication list that actually belongs to the patient, travels with the patient, and is readable by any clinician the patient chooses to share it with would prevent harm that is currently being absorbed as an acceptable cost of care. It is not acceptable. And it is not inevitable.',
    'That conviction — that the patient should carry their own verified health record, and that a single authoritative medication list is one of the most immediate safety improvements available to healthcare — is where FusionMSI’s patient-facing architecture begins. But it opens into something larger.',
    'Healthcare has a knowledge problem that predates AI by decades. We have always generated more clinical insight than we could capture. The emergency physician who worked out the right approach for a specific presentation in your patient population. The pharmacist who recognized a pattern in how a particular combination of medications behaves in elderly patients with reduced renal function. The charge nurse who developed an intuition for which triage assessments were understating acuity. This knowledge was real. It shaped outcomes. And when those people retired, transferred, or simply ended their shift, it left with them.',
    'Every generation of healthcare technology promised to fix this and fell short. Electronic health records captured what happened, not what was learned. Clinical decision support encoded what we already knew at the time it was built. Even the best AI tools available today are getting smarter — but their owners are getting smarter, not the institutions using them. A health system’s clinical interactions are becoming training signal for a model it will never own.',
    'This is the dynamic Nadella’s framework clarifies, and it is the dynamic that has to change.',
    'What makes the current moment different is not simply that AI has become more capable — though it has — but that we can now build systems where clinicians shape the learning inside the institution. Where a clinical pharmacist’s correction to an AI-assisted medication reconciliation doesn’t just fix one interaction; it feeds a governed loop that makes every subsequent interaction sharper. Where the pattern a triage nurse catches in how your specific community presents doesn’t disappear at shift change; it becomes part of how your institution’s AI approaches that presentation going forward. Where the accumulated judgment of your care team — reviewed and approved by the clinicians who generated it — shapes how your institution’s AI approaches the next patient.',
    'The governance architecture matters enormously here. The loop only improves in ways that a trained clinician has explicitly approved. A clinical domain expert reviews what the feedback cycle generated and decides what’s worth keeping. This isn’t a safety guardrail added as an afterthought — it is the mechanism that makes the loop trustworthy. And it means something important: the clinicians in this system are not being replaced. They are the engine. Their judgment is what the AI learns from. Their expertise becomes more strategically valuable as the loop compounds, not less.',
    'I want to be direct about the risk if we build this wrong. If healthcare organizations continue to treat AI as a subscription service rather than a loop their clinicians shape — if the clinical knowledge generated by physicians, nurses, and pharmacists across millions of patient encounters flows upward to a handful of foundational models rather than compounding inside the institutions that generated it — we will have made a version of the same error that hollowed out entire industrial economies in the first wave of globalization. The GDP numbers looked acceptable. The displacement was real and lasting. The communities that absorbed it are still absorbing it.',
    'Healthcare is too important and too local to tolerate that outcome. The emergency physician in a rural Wisconsin community, the clinical team at a mission hospital in Kenya, the pharmacist at an urban urgent care — each of them is generating clinical insight specific to their patient population, their formulary, their community. That insight should compound inside their institution. It should not be commoditized into a generic model that serves no one’s specific context particularly well.',
    'What I am working toward with FusionMSI is the version of this that Nadella describes at the economy-wide level, realized specifically in healthcare: a platform where every health system, every physician practice, every clinic and community hospital can build the cognitive loop that encodes its institutional knowledge — where clinicians shape the learning inside the organization that generated it. And where, at the foundation of all of it, the patient arrives at every encounter carrying their own verified record, their own accurate medication list, the accumulated context of their own health history — so that the clinical team can spend its time making decisions rather than reconstructing information that should never have been lost.',
    'The institutions that build this now will not simply have a better AI tool. They will have a fundamentally different kind of organization — one whose intelligence grows with every patient encounter, every clinical correction, every shift that a trained clinician spends inside the loop.',
    'And the patients who receive care at those institutions will be the direct beneficiaries of every improvement the loop has ever made — starting with something as basic, and as long overdue, as knowing what medications they are actually taking.',
  ],
};

export const FOOTER_DISCLOSURE =
  'PROVISIONAL PREVIEW — Fusion Medical SuperIntelligence (FusionMSI) is clinical decision support under active development. It does not replace physician judgment. Unverified content is watermarked. Doses and intervals on screen supersede spoken narration.';

export const PAGE_META = {
  'how-it-works': {
    slug: 'how-it-works',
    title: 'How it works',
    intro:
      'FusionMSI is designed for the emergency department: voice for navigation, screen for numbers, and physician-verified content — never improvised at the bedside.',
  },
  capabilities: {
    slug: 'capabilities',
    title: 'Capabilities',
    intro:
      'This preview includes bedside voice retrieval, a draft clinical library, and operator tools for import, review, and access control.',
  },
  access: {
    slug: 'access',
    title: 'Access & preview limits',
    intro:
      'Fusion Medical SuperIntelligence is invite-only during beta. Read what is — and is not — included in this provisional release.',
  },
};

export default {
  PRODUCT_NAME,
  PRODUCT_NAME_LONG,
  PROVISIONAL,
  HERO,
  HUB_PAGES,
  SECTIONS,
  CAPABILITY_GROUPS,
  VOICE_COMMANDS,
  LIMITATIONS,
  ACCESS,
  ABOUT,
  FOOTER_DISCLOSURE,
  PAGE_META,
};
